
import { useState ,useEffect} from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalCloseButton, Box, Flex, Text,
  Image, Spinner, SimpleGrid, IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";

const BORDER = "#EBEBEB";
const RED    = "#C0392B";

// ─── Product column definitions ───────────────────────────────────────────────
const productColumnDefs = [
  {
    headerName: "Product Name",
    field: "productName",
    minWidth: 200,
    flex: 2,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { display: "flex", alignItems: "center", color: "#1A202C" },
  },
  {
    headerName: "Product Class ID",
    field: "productClassId",
    minWidth: 150,
    flex: 1,
    sortable: true,
    filter: "agTextColumnFilter",
    cellStyle: { display: "flex", alignItems: "center", color: "#4A5568" },
  },
  {
    headerName: "Count",
    field: "count",
    minWidth: 100,
    maxWidth: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellStyle: { display: "flex", alignItems: "center", fontWeight: 600, color: RED },
  },
];

// ─── StoreDetailModal ─────────────────────────────────────────────────────────
export default function StoreDetailModal({
  isOpen,
  onClose,
  selectedStore,
  productLoading,
  imageUrls,       
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [loadedCount, setLoadedCount]   = useState(0);

  // Reset to first image when modal opens with new store
  const totalImages = imageUrls?.length ?? 0;
  const currentImage = imageUrls?.[currentImageIndex];

  const currentImageProducts = currentImage?.products ?? [];

  useEffect(() => {
  setImageLoading(false);
  setLoadedCount(0);
}, [currentImageIndex]);


  const handlePrev = () =>{
     setImageLoading(true);
     setLoadedCount(0); 
    setCurrentImageIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  }

  const handleNext = () =>{
     setImageLoading(true);
     setLoadedCount(0); 
    setCurrentImageIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  }

  const handleClose = () => {
    setCurrentImageIndex(0); 
    setImageLoading(false);
      setLoadedCount(0); 
    onClose();
  };
 // ── Called when each image loads ──
  const handleImageLoad = () => {
    setLoadedCount((prev) => {
      const next = prev + 1;
      if (next >= 2) setImageLoading(false); 
      return next;
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="12px" maxH="90vh">

        {/* ── Header ── */}
        <ModalHeader
          fontSize="md" fontWeight={700} color="gray.800"
          borderBottom={`1px solid ${BORDER}`} pb={3}
        >
          Store Details
          {selectedStore?.storeName && (
            <Text as="span" fontWeight={400} color="gray.500" ml={2} fontSize="sm">
              — {selectedStore.storeName}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />

        {/* ── Body ── */}
        <ModalBody py={5}>
          {productLoading ? (
            <Flex justify="center" align="center" h="300px">
              <Spinner size="xl" color="red.500" thickness="4px" />
            </Flex>
          ) : (
            <Flex direction="column" gap={6}>

              {/* ── Single Image Box ── */}
              <Box
                border={`1px solid ${BORDER}`}
                borderRadius="12px"
                overflow="hidden"
                bg="gray.50"
              >

                {/* Actual + Annotated side by side */}
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={0}>
                  {[
                    { label: "Actual Image",    src: currentImage?.actual },
                    { label: "Annotated Image", src: currentImage?.annotated },
                  ].map(({ label, src }, i) => (
                    <Box
                      key={label}
                      borderRight={i === 0 ? { md: `1px solid ${BORDER}` } : undefined}
                      borderBottom={i === 0 ? { base: `1px solid ${BORDER}`, md: "none" } : undefined}
                    >
                      {/* Label */}
                      <Text
                        fontSize="md" fontWeight={600} color="white"
                        textAlign="center" py={2}
                        borderBottom={`1px solid ${BORDER}`}
                        bg="#C0392B"
                      >
                        {label}
                      </Text>

                      {/* Image */}
                      <Flex justify="center" align="center" minH="260px" p={2}>
                        {imageLoading ? (
                           <Spinner size="lg" color="red.500" thickness="3px" />
                        ):src ?(
                          <Image
                            src={src}
                            alt={label}
                            maxH="260px"
                            objectFit="contain"
                            onLoad={handleImageLoad}
                            onError={handleImageLoad}
                          />
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            Image not available
                          </Text>
                        )}
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>

                {/* ── Chevron navigation ── */}
              
              {totalImages  && (
                <Flex
                  justify="flex-end"
                  align="center"
                  gap={2}
                  px={4}
                  py={2}
                  borderTop={`1px solid ${BORDER}`}
                  bg="white"
                >
                  <IconButton
                    icon={<ChevronLeftIcon boxSize={5} />}
                    onClick={handlePrev}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    aria-label="Previous image"
                    _hover={{ bg: "gray.100" }}
                  />

                  <Text fontSize="xs" fontWeight={600} color="gray.600" minW="32px" textAlign="center">
                    {currentImageIndex + 1} / {totalImages}
                  </Text>

                  <IconButton
                    icon={<ChevronRightIcon boxSize={5} />}
                    onClick={handleNext}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                    aria-label="Next image"
                    _hover={{ bg: "gray.100" }}
                  />
                </Flex>
              )}
                
              </Box>

              {/* ── Product Details AG Grid ── */}
              <Box>
                <Text fontWeight={600} fontSize="sm" color="gray.700" mb={3}>
                  Product Details
                </Text>
                <Box border={`1px solid ${BORDER}`} borderRadius="10px" overflow="hidden">
                  <AgGridWrapper
                    rowData={currentImageProducts}
                    columnDefs={productColumnDefs}
                    loading={productLoading}
                    height="280px"
                    noRowsMessage="No product data available."
                  />
                </Box>
              </Box>

            </Flex>
          )}
        </ModalBody>

      </ModalContent>
    </Modal>
  );
}